/**
 * Annotations-Dokument fuer bearbeitete Bildversionen.
 *
 * Alle Koordinaten liegen im Pixelraum des Originalbilds, unabhaengig vom
 * Zuschnitt (crop). Dadurch bleibt der Zuschnitt jederzeit aenderbar, ohne
 * dass Formen verschoben werden muessen.
 */

export const ANNOTATION_VERSION = 1 as const;

export type BoxShapeType = 'rect' | 'ellipse' | 'highlight' | 'pixelate' | 'redact' | 'spotlight';
export type ShapeType = BoxShapeType | 'arrow' | 'text' | 'marker';

export type ToolId = 'select' | ShapeType | 'crop';

interface ShapeBase {
  id: string;
  color: string;
  strokeWidth: number;
}

export interface BoxShape extends ShapeBase {
  type: BoxShapeType;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ArrowShape extends ShapeBase {
  type: 'arrow';
  /** [x1, y1, x2, y2] – Pfeilspitze am zweiten Punkt */
  points: [number, number, number, number];
}

export interface TextShape extends ShapeBase {
  type: 'text';
  x: number;
  y: number;
  text: string;
  fontSize: number;
}

export interface MarkerShape extends ShapeBase {
  type: 'marker';
  x: number;
  y: number;
  number: number;
  radius: number;
}

export type AnnotationShape = BoxShape | ArrowShape | TextShape | MarkerShape;

export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface AnnotationDocument {
  version: typeof ANNOTATION_VERSION;
  crop: CropRect | null;
  shapes: AnnotationShape[];
}

export const emptyAnnotationDocument = (): AnnotationDocument => ({
  version: ANNOTATION_VERSION,
  crop: null,
  shapes: [],
});

/** Parst gespeicherte Annotationen tolerant; liefert bei Fehlern ein leeres Dokument. */
export const parseAnnotationDocument = (raw: string | null | undefined): AnnotationDocument => {
  if (!raw) return emptyAnnotationDocument();
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.shapes)) {
      return emptyAnnotationDocument();
    }
    const crop = parsed.crop && typeof parsed.crop === 'object' ? (parsed.crop as CropRect) : null;
    return {
      version: ANNOTATION_VERSION,
      crop: crop && crop.width > 0 && crop.height > 0 ? crop : null,
      shapes: parsed.shapes as AnnotationShape[],
    };
  } catch {
    return emptyAnnotationDocument();
  }
};

/** Formen, die als Bildeffekt unter den eigentlichen Markierungen gezeichnet werden. */
export const isEffectShape = (shape: AnnotationShape): boolean =>
  shape.type === 'pixelate' || shape.type === 'redact' || shape.type === 'spotlight';

export const isBoxShape = (shape: AnnotationShape): shape is BoxShape =>
  shape.type === 'rect' ||
  shape.type === 'ellipse' ||
  shape.type === 'highlight' ||
  shape.type === 'pixelate' ||
  shape.type === 'redact' ||
  shape.type === 'spotlight';

export const ANNOTATION_COLORS: Array<{ value: string; label: string }> = [
  { value: '#e53935', label: 'Rot' },
  { value: '#fb8c00', label: 'Orange' },
  { value: '#fdd835', label: 'Gelb' },
  { value: '#43a047', label: 'Grün' },
  { value: '#1e88e5', label: 'Blau' },
  { value: '#ffffff', label: 'Weiß' },
  { value: '#000000', label: 'Schwarz' },
];

export type SizePreset = 'S' | 'M' | 'L';

export const SIZE_PRESETS: Record<SizePreset, { strokeWidth: number; fontSize: number; radius: number }> = {
  S: { strokeWidth: 2, fontSize: 16, radius: 14 },
  M: { strokeWidth: 4, fontSize: 24, radius: 20 },
  L: { strokeWidth: 7, fontSize: 36, radius: 28 },
};

export const sizePresetForStroke = (strokeWidth: number): SizePreset => {
  if (strokeWidth <= SIZE_PRESETS.S.strokeWidth) return 'S';
  if (strokeWidth <= SIZE_PRESETS.M.strokeWidth) return 'M';
  return 'L';
};

/** Gut lesbare Schriftfarbe (Weiß/Schwarz) auf einer gegebenen Hintergrundfarbe. */
export const contrastTextColor = (hex: string): string => {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return '#ffffff';
  const [r, g, b] = [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#000000' : '#ffffff';
};

export const newShapeId = (): string =>
  `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
